import type { ChangeEvent } from 'react';
import React, { useMemo, useState } from 'react';
import { Button, Input, Modal, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash.debounce';
import { useGetManyAddresses } from 'api/addresses/use-get-addresses.ts';
import type { AddressRes } from 'api/swagger';
import { useFindCountry } from 'shared/hooks/use-find-country.ts';
import { addressStatus } from 'shared/utils/address-status.ts';
import styles from './cargo-address-finder.module.scss';

interface ICargoAddressFinderProps {
  handleSelect: (address: AddressRes) => void;
  className?: string;
}

export const CargoAddressFinder: React.FC<ICargoAddressFinderProps> = (props) => {
  const { handleSelect, className } = props;
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const { data: addresses } = useGetManyAddresses({ search, isEnabled: true });

  const updateSearch = debounce((value: string) => {
    setSearch(value);
  }, 300);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateSearch(e.target.value);
  };

  const handleSelectItem = (address: AddressRes) => {
    handleSelect(address);
    handleClose();
  };

  return (
    <>
      <Button icon={<SearchOutlined />} className={className} onClick={handleOpen} />
      <Modal title='Search Address' open={isOpen} footer={null} onCancel={handleClose}>
        <Input placeholder={'Search...'} size='large' onChange={handleChange} />
        <div className={styles.container}>
          {addresses?.map((address) => (
            <CargoAddressFinderItem key={address.id} address={address} handleSelect={handleSelectItem} />
          ))}
        </div>
      </Modal>
    </>
  );
};

interface ICargoAddressFinderItemProps {
  address: AddressRes;
  handleSelect: (address: AddressRes) => void;
}
const CargoAddressFinderItem: React.FC<ICargoAddressFinderItemProps> = ({ address, handleSelect }) => {
  const country = useFindCountry(address.countryId);

  const icon = useMemo(
    () => addressStatus(address.googleLocationType, { detectionType: address.detectionType }),
    [address.detectionType, address.googleLocationType],
  );

  const handleClick = () => handleSelect(address);

  return (
    <div className={styles.addressItem} onClick={handleClick}>
      <div>
        <div>
          <Typography.Text color='d9d9d9'>{country?.code}</Typography.Text>
          {' - '}
          <Typography.Text strong>{address.name}</Typography.Text>
        </div>
        <div className={styles.addressBox}>
          <Typography.Text>{address.address}, </Typography.Text>
          <Typography.Text>{address.postCode} </Typography.Text>
          <Typography.Text>{address.city}, </Typography.Text>
          <Typography.Text>{country?.name} </Typography.Text>
        </div>
      </div>
      <div className={styles.icons}>{icon}</div>
    </div>
  );
};
